import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Calendar, Eye, ArrowRight, Tag } from "lucide-react";
import axios from "axios";
import { API } from "../App";

const BlogPage = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState("");

  const categories = [
    "All",
    "Maintenance Tips",
    "Performance",
    "Product Guides",
    "Industry News"
  ];

  useEffect(() => {
    const fetchPosts = async () => {
      setLoading(true);
      try {
        const params = selectedCategory && selectedCategory !== "All" 
          ? { category: selectedCategory } 
          : {};
        const response = await axios.get(`${API}/blog/posts`, { params });
        setPosts(response.data.posts);
      } catch (error) {
        console.error("Error fetching posts:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchPosts();
  }, [selectedCategory]);

  return (
    <div className="min-h-screen bg-background animate-fade-in" data-testid="blog-page">
      {/* Header */}
      <div className="bg-zinc-950 border-b border-zinc-800 py-16">
        <div className="container mx-auto px-4">
          <span className="text-primary font-medium uppercase tracking-wider">Knowledge Center</span>
          <h1 className="font-heading text-5xl md:text-6xl font-bold text-white uppercase tracking-tight mt-2" data-testid="page-title">
            Automotive Blog
          </h1>
          <p className="text-zinc-400 mt-4 max-w-xl text-lg">
            Expert tips, guides, and news to keep you informed about your vehicle.
          </p>
        </div>
      </div>

      <div className="container mx-auto px-4 py-12">
        {/* Categories */}
        <div className="flex flex-wrap gap-2 mb-12" data-testid="category-filters">
          {categories.map((cat) => (
            <button
              key={cat}
              onClick={() => setSelectedCategory(cat === "All" ? "" : cat)}
              className={`px-4 py-2 rounded transition-colors ${
                (cat === "All" && !selectedCategory) || selectedCategory === cat
                  ? "bg-primary text-white"
                  : "bg-zinc-900 text-zinc-400 hover:text-white hover:bg-zinc-800 border border-zinc-800"
              }`}
              data-testid={`category-${cat.toLowerCase().replace(/\s/g, '-')}`}
            >
              {cat}
            </button>
          ))}
        </div>

        {/* Posts Grid */}
        {loading ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="bg-zinc-900 aspect-[4/3] skeleton rounded"></div>
            ))}
          </div>
        ) : posts.length === 0 ? (
          <div className="text-center py-20" data-testid="no-posts">
            <p className="text-zinc-400 text-lg">No posts found in this category.</p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {posts.map((post, index) => (
              <article
                key={post.post_id}
                className={`blog-card ${index === 0 ? 'md:col-span-2 lg:col-span-2' : ''}`}
                data-testid={`blog-post-${post.post_id}`}
              >
                <Link to={`/blog/${post.slug}`}>
                  <div className={`blog-card-image ${index === 0 ? 'aspect-[2/1]' : 'aspect-video'}`}>
                    <img
                      src={post.image}
                      alt={post.title}
                      className="w-full h-full object-cover"
                    />
                  </div>
                </Link>
                <div className="p-6">
                  <div className="flex items-center gap-4 text-sm text-zinc-500 mb-3">
                    <span className="flex items-center gap-1">
                      <Calendar className="w-4 h-4" />
                      {new Date(post.created_at).toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric'
                      })}
                    </span>
                    <span className="flex items-center gap-1">
                      <Eye className="w-4 h-4" />
                      {post.views} views
                    </span>
                  </div>
                  
                  <span className="text-primary text-sm font-medium uppercase tracking-wider">
                    {post.category}
                  </span>
                  
                  <Link to={`/blog/${post.slug}`}>
                    <h2 className={`font-heading font-bold text-white uppercase mt-2 hover:text-primary transition-colors ${
                      index === 0 ? 'text-2xl md:text-3xl' : 'text-xl'
                    }`}>
                      {post.title}
                    </h2>
                  </Link>
                  
                  <p className="text-zinc-400 mt-3 line-clamp-2">
                    {post.excerpt}
                  </p>
                  
                  {post.tags && post.tags.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-4">
                      {post.tags.slice(0, 3).map((tag) => (
                        <span key={tag} className="flex items-center gap-1 text-xs text-zinc-500">
                          <Tag className="w-3 h-3" />
                          {tag}
                        </span>
                      ))}
                    </div>
                  )}
                  
                  <Link 
                    to={`/blog/${post.slug}`}
                    className="inline-flex items-center text-primary font-semibold mt-4 hover:translate-x-2 transition-transform"
                  >
                    Read More <ArrowRight className="w-4 h-4 ml-2" />
                  </Link>
                </div>
              </article>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default BlogPage;
